import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import type { ImageStatusMarker } from 'lib/community/discussions'
import styles from './DiscussionThread.module.css'

export interface LightboxImage {
  url: string
  alt: string
  width: number
  height: number
  /** Only on the author’s own view: "Not approved", shown on the photo here
   *  too (the photo stays in the set's navigation). */
  marker?: ImageStatusMarker | null
}

interface LightboxProps {
  images: LightboxImage[]
  /** The photo opened. */
  start: number
  onClose: () => void
}

const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'

/**
 * The focus trap's step: which of the dialog's `count` controls Tab (or Shift+Tab) moves to from
 * the one at `active` (-1 = focus is outside the dialog), or null to let the browser move within
 * it. Wraps last → first and first → last, so focus never leaves the dialog.
 */
export function trapFocusIndex(count: number, active: number, shift: boolean): number | null {
  if (count <= 0) {
    return null
  }
  if (active < 0) {
    return shift ? count - 1 : 0
  }
  if (!shift && active >= count - 1) {
    return 0
  }
  if (shift && active === 0) {
    return count - 1
  }
  return null
}

/**
 * A photo set full screen (design 10c), no library: a modal dialog over the page with the photo,
 * "n / N", previous / next, and close. Keyboard: Escape closes, ← → step through the set, Tab /
 * Shift+Tab cycle inside the dialog (`trapFocusIndex`; `aria-modal`). Focus moves to the close
 * button and returns to the photo that opened it; the page does not scroll behind it (body
 * overflow hidden while open). Rendered only while open — never in the server HTML.
 */
const Lightbox: React.VFC<LightboxProps> = ({ images, start, onClose }) => {
  const [index, setIndex] = useState(() => Math.min(Math.max(0, start), images.length - 1))
  const closeRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const count = images.length

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onCloseRef.current()
      } else if (e.key === 'ArrowRight') {
        setIndex(i => (i + 1) % count)
      } else if (e.key === 'ArrowLeft') {
        setIndex(i => (i - 1 + count) % count)
      } else if (e.key === 'Tab' && dialogRef.current) {
        const controls = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
        const next = trapFocusIndex(
          controls.length,
          controls.indexOf(document.activeElement as HTMLElement),
          e.shiftKey
        )
        if (next !== null) {
          e.preventDefault()
          controls[next].focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
      opener?.focus?.()
    }
  }, [count])

  const image = images[index]
  if (!image) {
    return null
  }
  return createPortal(
    // Clicking the backdrop (not the photo or a control) closes, like Escape.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={dialogRef}
      className={styles.lightbox}
      role="dialog"
      aria-modal="true"
      aria-label={`Photo ${index + 1} of ${count}${image.marker ? ` (${image.marker.label})` : ''}`}
      onClick={e => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      {count > 1 && <span className={styles.lightboxCount}>{`${index + 1} / ${count}`}</span>}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        className={clsx(styles.lightboxImage, {
          [styles.lightboxImageHidden]: image.marker?.tone === 'hidden',
        })}
        src={image.url}
        alt={image.alt}
        width={image.width}
        height={image.height}
        loading="lazy"
        decoding="async"
      />
      {image.marker && (
        <span className={clsx(styles.statusBadge, styles.lightboxBadge)}>{image.marker.label}</span>
      )}
      {count > 1 && (
        <>
          <button
            type="button"
            className={clsx(styles.lightboxNav, styles.lightboxPrev)}
            aria-label="Previous photo"
            onClick={() => setIndex(i => (i - 1 + count) % count)}
          >
            {'‹'}
          </button>
          <button
            type="button"
            className={clsx(styles.lightboxNav, styles.lightboxNext)}
            aria-label="Next photo"
            onClick={() => setIndex(i => (i + 1) % count)}
          >
            {'›'}
          </button>
        </>
      )}
      <button
        ref={closeRef}
        type="button"
        className={styles.lightboxClose}
        aria-label="Close"
        onClick={onClose}
      >
        {'×'}
      </button>
    </div>,
    document.body
  )
}

export default Lightbox
